import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  socialOAuthSettingsService,
  type SocialOAuthProvider,
  type SocialOAuthSettingsDetail,
  type SocialOAuthSettingsListItem,
  type SocialOAuthValidationStatus,
  type SocialOAuthSettingsRequest,
  type SocialOAuthValidationResponse,
} from '@/services/auth/social-oauth-settings.service';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Apple,
  CheckCircle2,
  Github,
  Globe,
  Loader2,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { MicrosoftIcon } from '@/components/icons/MicrosoftIcon';
import clsx from 'clsx';

type ActionState = {
  saving: boolean;
  validating: boolean;
  testing: boolean;
  deleting: boolean;
};

type SocialFormValues = {
  enabled: boolean;
  mode: 'test' | 'live';
  client_id: string;
  client_secret: string;
  scopes: string;
  prompt: string;
  login_hint_default: string;
  redirect_uri_override: string;
  team_id: string;
  key_id: string;
  private_key: string;
  auto_rotate_secret: boolean;
  client_secret_static: string;
  org_whitelist: string;
  enterprise_host: string;
};

type ValidationResult = {
  status: 'success' | 'error';
  message: string;
  correlationId?: string;
} | null;

const PROVIDERS: SocialOAuthProvider[] = ['google', 'github', 'apple', 'azure'];

const EMPTY_PROVIDER_LIST: SocialOAuthSettingsListItem[] = PROVIDERS.map((provider) => ({
  provider,
  enabled: false,
  validation_status: 'unvalidated',
}));

const DEFAULT_FORM_VALUES: SocialFormValues = {
  enabled: false,
  mode: 'test',
  client_id: '',
  client_secret: '',
  scopes: '',
  prompt: '',
  login_hint_default: '',
  redirect_uri_override: '',
  team_id: '',
  key_id: '',
  private_key: '',
  auto_rotate_secret: false,
  client_secret_static: '',
  org_whitelist: '',
  enterprise_host: '',
};

function extractErrorMessage(error: unknown, fallback: string): string {
  if (!error) {
    return fallback;
  }

  if (
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object'
  ) {
    const response = error.response as { data?: unknown; message?: string };
    if (
      response.data &&
      typeof response.data === 'object' &&
      response.data !== null &&
      'message' in response.data &&
      typeof (response.data as { message?: string }).message === 'string'
    ) {
      return (response.data as { message?: string }).message as string;
    }
    if (typeof response.message === 'string') {
      return response.message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

function formatDate(date?: string | null): string | null {
  if (!date) {
    return null;
  }
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  } catch {
    return date;
  }
}

function parseListValue(value?: string | string[] | null): string {
  if (!value) {
    return '';
  }
  if (Array.isArray(value)) {
    return value.join('\n');
  }
  return value;
}

function toOptionalArray(value: string): string[] | undefined {
  const tokens = value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
  return tokens.length > 0 ? tokens : undefined;
}

function sanitizePayload(payload: SocialOAuthSettingsRequest): SocialOAuthSettingsRequest {
  return Object.fromEntries(
    Object.entries(payload).filter(([_key, value]) => {
      if (value === undefined) {
        return false;
      }
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (value && typeof value === 'object') {
        return Object.keys(value).length > 0;
      }
      return value !== null;
    }),
  );
}

interface ProviderBadgeProps {
  listItem?: SocialOAuthSettingsListItem;
}

function ProviderBadge({ listItem }: ProviderBadgeProps) {
  const { t } = useTranslation();
  const status = listItem?.validation_status;
  const enabled = listItem?.enabled ?? false;

  if (!listItem) {
    return null;
  }

  if (!enabled) {
    return (
      <Badge variant="outline" className="text-xs">
        {t('settings.socialAuth.status.disabled')}
      </Badge>
    );
  }

  if (status === 'valid') {
    return (
      <Badge variant="secondary" className="text-xs flex items-center gap-1">
        <ShieldCheck className="h-3 w-3" />
        {t('settings.socialAuth.status.valid')}
      </Badge>
    );
  }

  if (status === 'invalid') {
    return (
      <Badge variant="destructive" className="text-xs flex items-center gap-1">
        <ShieldAlert className="h-3 w-3" />
        {t('settings.socialAuth.status.invalid')}
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className="text-xs">
      {t('settings.socialAuth.status.pending')}
    </Badge>
  );
}

interface ProviderFormProps {
  provider: SocialOAuthProvider;
  listItem?: SocialOAuthSettingsListItem;
  detail?: SocialOAuthSettingsDetail;
  isLoading: boolean;
  actionState: ActionState;
  validationResult: ValidationResult;
  onResetValidation: () => void;
  onSubmit: (_provider: SocialOAuthProvider, _payload: SocialOAuthSettingsRequest, _isCreate: boolean) => Promise<void>;
  onValidate: (_provider: SocialOAuthProvider, _payload: SocialOAuthSettingsRequest) => Promise<SocialOAuthValidationResponse>;
  onTest: (_provider: SocialOAuthProvider, _payload: SocialOAuthSettingsRequest) => Promise<SocialOAuthValidationResponse>;
  onDelete: (_provider: SocialOAuthProvider) => Promise<void>;
}

function SocialOAuthProviderForm({
  provider,
  listItem,
  detail,
  isLoading,
  actionState,
  validationResult,
  onResetValidation,
  onSubmit,
  onValidate,
  onTest,
  onDelete,
}: ProviderFormProps) {
  const { t } = useTranslation();

  const normalizedDetail = useMemo<Record<string, unknown>>(
    () => (detail ? { ...detail } : {}),
    [detail],
  );

  const formValues = useMemo<SocialFormValues>(() => {
    if (!detail) {
      if (provider === 'azure') {
        return {
          ...DEFAULT_FORM_VALUES,
          scopes: 'openid\nprofile\nemail\nUser.Read',
        };
      }
      return DEFAULT_FORM_VALUES;
    }

    const raw = detail as unknown as Record<string, unknown>;
    const tenantConfig = (detail.tenant_config ?? {}) as Record<string, unknown>;
    const clientId =
      (raw.client_id as string | undefined) ??
      (detail.client_id_masked as string | undefined) ??
      (raw.client_id_masked as string | undefined) ??
      '';
    return {
      enabled: Boolean(detail.enabled ?? raw.enabled),
      mode: (detail.mode as 'test' | 'live') ?? (raw.mode as 'test' | 'live') ?? 'test',
      client_id: clientId,
      client_secret: '',
      scopes: Array.isArray(detail.scopes) ? detail.scopes.join('\n') : '',
      prompt: (detail.prompt as string | undefined) ?? '',
      login_hint_default: (detail.login_hint_default as string | undefined) ?? '',
      redirect_uri_override: (detail.redirect_uri_override as string | undefined) ?? '',
      team_id: (tenantConfig.team_id as string | undefined) ?? (raw.team_id as string | undefined) ?? '',
      key_id: (tenantConfig.key_id as string | undefined) ?? (raw.key_id as string | undefined) ?? '',
      private_key: '',
      auto_rotate_secret:
        Boolean((raw.auto_rotate_secret as boolean | undefined) ?? (tenantConfig.auto_rotate_secret as boolean | undefined)),
      client_secret_static: '',
      org_whitelist: parseListValue(
        (tenantConfig.org_whitelist as string[]) ?? (raw.org_whitelist as string[] | undefined),
      ),
      enterprise_host: (tenantConfig.enterprise_host as string | undefined) ?? (raw.enterprise_host as string | undefined) ?? '',
    };
  }, [detail, provider]);

  const form = useForm<SocialFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const isConfigured = useMemo(() => {
    if (!detail) {
      return false;
    }
    const raw = normalizedDetail;
    return Boolean(
      detail.created_at ||
        detail.updated_at ||
        detail.client_id_masked ||
        raw.client_id ||
        detail.has_client_secret ||
        detail.last_validated_at,
    );
  }, [detail, normalizedDetail]);

  const redirectUri =
    detail?.redirect_uri ??
    normalizedDetail['current_redirect_uri']?.toString() ??
    listItem?.redirect_uri ??
    '';

  const lastValidatedAt = formatDate(
    detail?.last_validated_at ??
      normalizedDetail['last_validated_at']?.toString?.() ??
      listItem?.last_validated_at,
  );

  useEffect(() => {
    form.reset(formValues);
  }, [formValues, form]);

  const handleSubmit = form.handleSubmit(async (values) => {
    const payload: SocialOAuthSettingsRequest = {
      enabled: values.enabled,
      mode: values.mode,
      client_id: values.client_id.trim(),
      client_secret: values.client_secret.trim(),
      scopes: toOptionalArray(values.scopes),
      prompt: values.prompt.trim() || undefined,
      login_hint_default: values.login_hint_default.trim() || undefined,
      redirect_uri_override: values.redirect_uri_override.trim() || undefined,
    };

    if (provider === 'github') {
      const tenantConfig: Record<string, unknown> = {};
      const orgWhitelist = toOptionalArray(values.org_whitelist);
      if (orgWhitelist) {
        tenantConfig.org_whitelist = orgWhitelist;
      }
      if (values.enterprise_host.trim()) {
        tenantConfig.enterprise_host = values.enterprise_host.trim();
      }
      if (Object.keys(tenantConfig).length > 0) {
        payload.tenant_config = tenantConfig;
      }
    }

    if (provider === 'apple') {
      if (values.team_id.trim()) {
        payload.team_id = values.team_id.trim();
      }
      if (values.key_id.trim()) {
        payload.key_id = values.key_id.trim();
      }
      if (values.private_key.trim()) {
        payload.private_key = values.private_key.trim();
      }
      if (values.client_secret_static.trim()) {
        payload.client_secret = values.client_secret_static.trim();
      }
      payload.auto_rotate_secret = values.auto_rotate_secret;
    }

    if (provider === 'google') {
      // For Google, allow scopes without additional config (already handled above)
    }

    const sanitized = sanitizePayload(payload);
    try {
      await onSubmit(provider, sanitized, !isConfigured);
      onResetValidation();
      form.reset({
        ...form.getValues(),
        client_secret: '',
        private_key: '',
        client_secret_static: '',
      });
    } catch (_error) {
      // Errors are surfaced via toast; keep form values intact for correction
    }
  });

  const handleValidate = async () => {
    const values = form.getValues();
    const payload: SocialOAuthSettingsRequest = sanitizePayload({
      enabled: values.enabled,
      mode: values.mode,
      client_id: values.client_id.trim(),
      client_secret: provider === 'apple' ? values.client_secret_static.trim() : values.client_secret.trim(),
      scopes: toOptionalArray(values.scopes),
      prompt: values.prompt.trim() || undefined,
      login_hint_default: values.login_hint_default.trim() || undefined,
      redirect_uri_override: values.redirect_uri_override.trim() || undefined,
      team_id: provider === 'apple' ? values.team_id.trim() || undefined : undefined,
      key_id: provider === 'apple' ? values.key_id.trim() || undefined : undefined,
      private_key: provider === 'apple' ? values.private_key.trim() || undefined : undefined,
      auto_rotate_secret: provider === 'apple' ? values.auto_rotate_secret : undefined,
      tenant_config:
        provider === 'github'
          ? sanitizePayload({
              org_whitelist: toOptionalArray(values.org_whitelist),
              enterprise_host: values.enterprise_host.trim() || undefined,
            })
          : undefined,
    });

    const result = await onValidate(provider, payload);
    return result;
  };

  const handleTest = async () => {
    const values = form.getValues();
    const payload: SocialOAuthSettingsRequest = sanitizePayload({
      enabled: values.enabled,
      mode: values.mode,
      client_id: values.client_id.trim(),
      client_secret: provider === 'apple' ? values.client_secret_static.trim() : values.client_secret.trim(),
      scopes: toOptionalArray(values.scopes),
      prompt: values.prompt.trim() || undefined,
      login_hint_default: values.login_hint_default.trim() || undefined,
      redirect_uri_override: values.redirect_uri_override.trim() || undefined,
      team_id: provider === 'apple' ? values.team_id.trim() || undefined : undefined,
      key_id: provider === 'apple' ? values.key_id.trim() || undefined : undefined,
      private_key: provider === 'apple' ? values.private_key.trim() || undefined : undefined,
      auto_rotate_secret: provider === 'apple' ? values.auto_rotate_secret : undefined,
      tenant_config:
        provider === 'github'
          ? sanitizePayload({
              org_whitelist: toOptionalArray(values.org_whitelist),
              enterprise_host: values.enterprise_host.trim() || undefined,
            })
          : undefined,
    });

    const result = await onTest(provider, payload);
    return result;
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        t('settings.socialAuth.messages.confirmDelete', {
          provider: t(`settings.socialAuth.providers.${provider}.name`),
        }),
      )
    ) {
      return;
    }
    await onDelete(provider);
    onResetValidation();
  };

  const ProviderIcon =
    provider === 'google' ? Globe : provider === 'github' ? Github : provider === 'apple' ? Apple : MicrosoftIcon;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ProviderIcon className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-lg font-semibold">
              {t(`settings.socialAuth.providers.${provider}.title`)}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            {t(`settings.socialAuth.providers.${provider}.description`)}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {lastValidatedAt ? (
            <span className="text-xs text-muted-foreground">
              {t('settings.socialAuth.labels.lastValidated', { date: lastValidatedAt })}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">{t('settings.socialAuth.status.neverValidated')}</span>
          )}
          <ProviderBadge listItem={listItem} />
        </div>
      </div>

      {validationResult ? (
        <Alert variant={validationResult.status === 'success' ? 'default' : 'destructive'}>
          <AlertDescription className="flex items-center gap-2">
            {validationResult.status === 'success' ? (
              <ShieldCheck className="h-4 w-4 text-emerald-500" />
            ) : (
              <ShieldAlert className="h-4 w-4 text-destructive" />
            )}
            <span>{validationResult.message}</span>
            {validationResult.correlationId ? (
              <span className="text-xs text-muted-foreground">
                ({t('settings.socialAuth.labels.correlationId', { id: validationResult.correlationId })})
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : (
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="text-sm font-medium">{t('settings.socialAuth.fields.enabled')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('settings.socialAuth.helpers.enabled')}
                </p>
              </div>
              <Switch checked={form.watch('enabled')} onCheckedChange={(checked) => form.setValue('enabled', checked)} />
            </div>

            <div className="rounded-lg border p-4">
              <p className="text-sm font-medium">{t('settings.socialAuth.fields.mode.label')}</p>
              <p className="text-xs text-muted-foreground mb-3">
                {t('settings.socialAuth.helpers.mode')}
              </p>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    value="test"
                    checked={form.watch('mode') === 'test'}
                    onChange={() => form.setValue('mode', 'test')}
                  />
                  {t('settings.socialAuth.fields.mode.test')}
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    value="live"
                    checked={form.watch('mode') === 'live'}
                    onChange={() => form.setValue('mode', 'live')}
                  />
                  {t('settings.socialAuth.fields.mode.live')}
                </label>
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('settings.socialAuth.fields.clientId')}
              </label>
              <Input placeholder={t('settings.socialAuth.placeholders.clientId')} {...form.register('client_id')} />
              {detail?.client_id_masked ? (
                <p className="text-xs text-muted-foreground">
                  {t('settings.socialAuth.helpers.clientIdMasked', { value: detail.client_id_masked })}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('settings.socialAuth.fields.clientSecret')}
              </label>
              <Input
                type="password"
                placeholder={t('settings.socialAuth.placeholders.clientSecret')}
                {...form.register(provider === 'apple' ? 'client_secret_static' : 'client_secret')}
              />
              <p className="text-xs text-muted-foreground">
                {t('settings.socialAuth.helpers.clientSecret')}
              </p>
            </div>
          </div>

          {provider === 'apple' ? (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('settings.socialAuth.fields.teamId')}</label>
                <Input placeholder="ABCD123456" {...form.register('team_id')} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('settings.socialAuth.fields.keyId')}</label>
                <Input placeholder="AA11BB22CC" {...form.register('key_id')} />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="text-sm font-medium">
                  {t('settings.socialAuth.fields.privateKey')}
                </label>
                <Textarea
                  placeholder="-----BEGIN PRIVATE KEY-----"
                  className="font-mono"
                  rows={6}
                  {...form.register('private_key')}
                />
                <p className="text-xs text-muted-foreground">
                  {t('settings.socialAuth.helpers.privateKey')}
                </p>
              </div>
              <div className="md:col-span-2 flex items-center justify-between rounded-lg border p-4">
                <div>
                  <p className="text-sm font-medium">
                    {t('settings.socialAuth.fields.autoRotateSecret')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('settings.socialAuth.helpers.autoRotateSecret')}
                  </p>
                </div>
                <Switch
                  checked={form.watch('auto_rotate_secret')}
                  onCheckedChange={(checked) => form.setValue('auto_rotate_secret', checked)}
                />
              </div>
            </div>
          ) : null}

          {provider === 'github' ? (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t('settings.socialAuth.fields.enterpriseHost')}
                </label>
                <Input placeholder="https://github.example.com" {...form.register('enterprise_host')} />
              </div>
              <div className="md:col-span-2 space-y-2">
                <label className="text-sm font-medium">
                  {t('settings.socialAuth.fields.orgWhitelist')}
                </label>
                <Textarea
                  placeholder={t('settings.socialAuth.placeholders.orgWhitelist')}
                  rows={3}
                  {...form.register('org_whitelist')}
                />
                <p className="text-xs text-muted-foreground">
                  {t('settings.socialAuth.helpers.orgWhitelist')}
                </p>
              </div>
            </div>
          ) : null}

          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('settings.socialAuth.fields.scopes')}
            </label>
            <Textarea
              rows={3}
              placeholder={t('settings.socialAuth.placeholders.scopes')}
              {...form.register('scopes')}
            />
            <p className="text-xs text-muted-foreground">
              {t('settings.socialAuth.helpers.scopes')}
            </p>
          </div>

  <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('settings.socialAuth.fields.prompt')}
              </label>
              <Input placeholder={t('settings.socialAuth.placeholders.prompt')} {...form.register('prompt')} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('settings.socialAuth.fields.loginHint')}
              </label>
              <Input
                placeholder={t('settings.socialAuth.placeholders.loginHint')}
                {...form.register('login_hint_default')}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium">
                {t('settings.socialAuth.fields.redirectOverride')}
              </label>
              <Input
                placeholder={t('settings.socialAuth.placeholders.redirectOverride')}
                {...form.register('redirect_uri_override')}
              />
              <p className="text-xs text-muted-foreground">
                {t('settings.socialAuth.helpers.redirectOverride')}
              </p>
            </div>
          </div>

          <div className="rounded-lg border p-4 bg-muted/30">
            <p className="text-sm font-medium mb-2">
              {t('settings.socialAuth.labels.currentRedirect')}
            </p>
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <code className="text-xs break-all bg-background px-2 py-1 rounded border">{redirectUri}</code>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  void navigator.clipboard.writeText(redirectUri);
                  toast.success(t('settings.socialAuth.messages.copiedRedirect'));
                }}
              >
                {t('settings.socialAuth.actions.copyRedirect')}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t('settings.socialAuth.helpers.redirectInfo')}
            </p>
          </div>

          {detail?.issues && detail.issues.length > 0 ? (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 space-y-2">
              <p className="text-sm font-semibold text-destructive">
                {t('settings.socialAuth.labels.outstandingIssues')}
              </p>
              <ul className="list-disc pl-4 text-xs text-destructive">
                {detail.issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <Separator />

          <div className="flex flex-wrap gap-3">
            <Button type="submit" disabled={actionState.saving}>
              {actionState.saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {t('settings.socialAuth.actions.save')}
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={actionState.validating}
              onClick={() => {
                handleValidate()
                  .then((result: SocialOAuthValidationResponse) => {
                    toast.success(
                      result.meta?.message ??
                        result.data?.message ??
                        t('settings.socialAuth.messages.validationSuccess'),
                    );
                  })
                  .catch((error) => {
                    toast.error(
                      extractErrorMessage(error, t('settings.socialAuth.messages.validationError')),
                    );
                  });
              }}
            >
              {actionState.validating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('settings.socialAuth.actions.validating')}
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  {t('settings.socialAuth.actions.validate')}
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={actionState.testing}
              onClick={() => {
                handleTest()
                  .then((response: SocialOAuthValidationResponse) => {
                    toast.success(
                      response.meta?.message ??
                        response.data?.message ??
                        t('settings.socialAuth.messages.testSuccess'),
                    );
                  })
                  .catch((error) => {
                    toast.error(
                      extractErrorMessage(error, t('settings.socialAuth.messages.testError')),
                    );
                  });
              }}
            >
              {actionState.testing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('settings.socialAuth.actions.testing')}
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('settings.socialAuth.actions.test')}
                </>
              )}
            </Button>
            {isConfigured ? (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                disabled={actionState.deleting}
                onClick={() => {
                  handleDelete().catch((error) => {
                    toast.error(
                      extractErrorMessage(error, t('settings.socialAuth.messages.deleteError')),
                    );
                  });
                }}
              >
                {actionState.deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('settings.socialAuth.actions.deleting')}
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('settings.socialAuth.actions.delete')}
                  </>
                )}
              </Button>
            ) : null}
          </div>
        </form>
      )}
    </div>
  );
}

export function SocialOAuthConfiguration({ className }: { className?: string }) {
  const { t } = useTranslation();
  const [list, setList] = useState<SocialOAuthSettingsListItem[]>(EMPTY_PROVIDER_LIST);
  const [details, setDetails] = useState<Partial<Record<SocialOAuthProvider, SocialOAuthSettingsDetail>>>({});
  const [selectedProvider, setSelectedProvider] = useState<SocialOAuthProvider>('google');
  const [isListLoading, setIsListLoading] = useState(true);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [actionState, setActionState] = useState<ActionState>({
    saving: false,
    validating: false,
    testing: false,
    deleting: false,
  });
  const [validationResult, setValidationResult] = useState<Record<SocialOAuthProvider, ValidationResult>>({
    google: null,
    github: null,
    apple: null,
    azure: null,
  });
  const [loadError, setLoadError] = useState<string | null>(null);
  const requestedDetailsRef = useRef<Set<SocialOAuthProvider>>(new Set());

  const resetValidation = (provider: SocialOAuthProvider) => {
    setValidationResult((prev) => ({
      ...prev,
      [provider]: null,
    }));
  };

  const extractStatus = (error: unknown): number | undefined => {
    if (!error || typeof error !== 'object') {
      return undefined;
    }

    const maybeResponse =
      'response' in error && error.response && typeof error.response === 'object' ? error.response : null;

    if (maybeResponse && typeof (maybeResponse as { status?: unknown }).status === 'number') {
      return (maybeResponse as { status: number }).status;
    }

    const maybeData = maybeResponse && 'data' in maybeResponse ? (maybeResponse as { data?: unknown }).data : undefined;
    if (maybeData && typeof maybeData === 'object') {
      if (typeof (maybeData as { status?: unknown }).status === 'number') {
        return (maybeData as { status: number }).status;
      }

      if (Array.isArray((maybeData as { errors?: unknown }).errors)) {
        const firstError = ((maybeData as { errors: unknown[] }).errors ?? [])[0] as
          | { status?: number | string }
          | undefined;
        if (firstError) {
          if (typeof firstError.status === 'number') {
            return firstError.status;
          }
          if (typeof firstError.status === 'string' && /^\d{3}$/.test(firstError.status)) {
            return Number(firstError.status);
          }
        }
      }
    }

    return undefined;
  };

  const loadList = useCallback(async () => {
    try {
      setIsListLoading(true);
      setLoadError(null);
      const settings = await socialOAuthSettingsService.listSettings();
      const normalized = PROVIDERS.map((provider) => {
        const existing = settings.find((item) => item.provider === provider);
        return (
          existing ?? ({
            provider,
            enabled: false,
            validation_status: 'unvalidated' as SocialOAuthValidationStatus,
          } satisfies SocialOAuthSettingsListItem)
        );
      });
      setList(normalized);
      if (!normalized.find((item) => item.provider === selectedProvider)) {
        setSelectedProvider(normalized[0]?.provider ?? 'google');
      }
    } catch (error) {
      const status = extractStatus(error);
      if (status === 404) {
        setList(EMPTY_PROVIDER_LIST);
        setLoadError(null);
        return;
      }
      if (status === 403) {
        setList(EMPTY_PROVIDER_LIST);
        setLoadError(t('settings.socialAuth.messages.forbidden'));
        return;
      }
      const message = extractErrorMessage(error, t('settings.socialAuth.messages.loadFailed'));
      setLoadError(message);
    } finally {
      setIsListLoading(false);
    }
  }, [selectedProvider, t]);

  const loadDetail = useCallback(
    async (provider: SocialOAuthProvider) => {
      if (requestedDetailsRef.current.has(provider)) {
        return;
      }
      requestedDetailsRef.current.add(provider);

      try {
        setIsDetailLoading(true);
        const detail = await socialOAuthSettingsService.getSettings(provider);
        console.log('Loaded OAuth detail', provider, detail);
        setDetails((prev) => ({
        ...prev,
        [provider]: detail,
      }));
      } catch (error) {
        console.error('Failed to load provider detail', provider, error);
        const status = extractStatus(error);
        if (status === 404) {
          setDetails((prev) => ({
            ...prev,
            [provider]: undefined,
          }));
          return;
        } else {
          toast.error(extractErrorMessage(error, t('settings.socialAuth.messages.loadFailed')));
        }
      } finally {
        setIsDetailLoading(false);
        requestedDetailsRef.current.delete(provider);
      }
    },
    [t],
  );

  useEffect(() => {
    loadList().catch(() => {
      /* handled in loadList */
    });
  }, [loadList]);

  useEffect(() => {
    const hasDetail = Object.prototype.hasOwnProperty.call(details, selectedProvider);
    if (!hasDetail && !isDetailLoading) {
      loadDetail(selectedProvider).catch(() => {
        /* handled */
      });
    }
  }, [selectedProvider, details, loadDetail, isDetailLoading]);

  const withActionState = async <T extends void | SocialOAuthValidationResponse>(
    key: keyof ActionState,
    provider: SocialOAuthProvider,
    action: () => Promise<T>,
    successMessage?: string,
    errorMessage?: string,
  ): Promise<T> => {
    setActionState((prev) => ({ ...prev, [key]: true }));
    try {
      const result = await action();
      if (successMessage) {
        toast.success(successMessage);
      }
      if (result && typeof result === 'object' && 'data' in result) {
        const payload = result as SocialOAuthValidationResponse;
        const status = payload.data?.valid ?? payload.data?.success ?? false;
        setValidationResult((prev) => ({
          ...prev,
          [provider]: {
            status: status ? 'success' : 'error',
            message:
              payload.data?.message ??
              payload.meta?.message ??
              (status
                ? t('settings.socialAuth.messages.validationSuccess')
                : t('settings.socialAuth.messages.validationError')),
            correlationId: payload.meta?.correlation_id ?? (payload.data?.meta?.correlation_id as string | undefined),
          },
        }));
      } else if (key === 'saving' || key === 'deleting') {
        resetValidation(provider);
      }
      return result;
    } catch (error) {
      const message = extractErrorMessage(error, errorMessage ?? t('common.errorLoadingData'));
      toast.error(message);
      setValidationResult((prev) => ({
        ...prev,
        [provider]:
          key === 'validating' || key === 'testing'
            ? {
                status: 'error',
                message,
              }
            : prev[provider],
      }));
      throw error;
    } finally {
      setActionState((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSubmit = async (
    provider: SocialOAuthProvider,
    payload: SocialOAuthSettingsRequest,
    isCreate: boolean,
  ) => {
    await withActionState(
      'saving',
      provider,
      async () => {
        if (isCreate) {
          await socialOAuthSettingsService.createSettings(provider, payload);
        } else {
          await socialOAuthSettingsService.updateSettings(provider, payload);
        }
        await loadList();
        await loadDetail(provider);
      },
      t('settings.socialAuth.messages.saveSuccess'),
      t('settings.socialAuth.messages.saveError'),
    );
  };

  const handleValidate = (
    provider: SocialOAuthProvider,
    payload: SocialOAuthSettingsRequest,
  ): Promise<SocialOAuthValidationResponse> =>
    withActionState<SocialOAuthValidationResponse>(
      'validating',
      provider,
      () => socialOAuthSettingsService.validateSettings(provider, payload),
    );

  const handleTest = (
    provider: SocialOAuthProvider,
    payload: SocialOAuthSettingsRequest,
  ): Promise<SocialOAuthValidationResponse> =>
    withActionState<SocialOAuthValidationResponse>(
      'testing',
      provider,
      () => socialOAuthSettingsService.testSettings(provider, payload),
    );

  const handleDelete = async (provider: SocialOAuthProvider) => {
    await withActionState(
      'deleting',
      provider,
      async () => {
        await socialOAuthSettingsService.deleteSettings(provider);
        await loadList();
        setDetails((prev) => ({
          ...prev,
          [provider]: undefined,
        }));
      },
      t('settings.socialAuth.messages.deleteSuccess'),
      t('settings.socialAuth.messages.deleteError'),
    );
  };

  const renderTabs = () => (
    <Tabs value={selectedProvider} onValueChange={(value) => setSelectedProvider(value as SocialOAuthProvider)}>
      <TabsList className="grid h-auto w-full grid-cols-2 gap-2 md:grid-cols-4">
        {PROVIDERS.map((provider) => {
          const listItem = list.find((item) => item.provider === provider);
          const Icon =
            provider === 'google' ? Globe : provider === 'github' ? Github : provider === 'apple' ? Apple : MicrosoftIcon;
          return (
            <TabsTrigger
              key={provider}
              value={provider}
              className={clsx(
                'flex items-center justify-center gap-2 px-3 py-2 text-sm',
                selectedProvider === provider && 'shadow-sm',
              )}
            >
              <Icon className="h-4 w-4" />
              <span>{t(`settings.socialAuth.providers.${provider}.name`)}</span>
              <ProviderBadge listItem={listItem} />
            </TabsTrigger>
          );
        })}
      </TabsList>
      {PROVIDERS.map((provider) => (
        <TabsContent
          key={`${provider}-${details[provider]?.updated_at ?? details[provider]?.created_at ?? 'blank'}`}
          value={provider}
          className="mt-6"
        >
          <SocialOAuthProviderForm
            provider={provider}
            listItem={list.find((item) => item.provider === provider)}
            detail={details[provider]}
            isLoading={isDetailLoading && selectedProvider === provider && !details[provider]}
            actionState={actionState}
            validationResult={validationResult[provider]}
            onResetValidation={() => resetValidation(provider)}
            onSubmit={handleSubmit}
            onValidate={handleValidate}
            onTest={handleTest}
            onDelete={handleDelete}
          />
        </TabsContent>
      ))}
    </Tabs>
  );

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{t('settings.socialAuth.title')}</CardTitle>
        <CardDescription>{t('settings.socialAuth.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loadError ? (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4" />
              <span>{loadError}</span>
            </AlertDescription>
          </Alert>
        ) : isListLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        ) : list.length === 0 ? (
          <Alert>
            <AlertDescription className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4" />
              <span>{t('settings.socialAuth.messages.noProviders')}</span>
            </AlertDescription>
          </Alert>
        ) : (
          renderTabs()
        )}
      </CardContent>
    </Card>
  );
}
